import { useEffect, useRef, useState } from 'react';
import { AlertTriangle, Check, Loader2, Save, Trash2, Upload, UploadCloud } from 'lucide-react';
import { apiService, PackItemDTO, PackTextureRef, PackGeometryRef } from '@/services/api.service';
import { clearPackTextureCache } from '@/services/packAssets';
import { useUnsavedDraft } from '@/stores/unsavedDrafts.store';
import { PackThumbnail } from './PackThumbnail';
import { SearchPicker } from './SearchPicker';
import { RecipeEditor } from './RecipeEditor';
import styles from './PackForm.module.scss';

type ItemTab = 'info' | 'tool' | 'armor' | 'food' | 'recipe';
const TABS: { id: ItemTab; label: string }[] = [
  { id: 'info', label: 'Information' },
  { id: 'tool', label: 'Outil / Arme' },
  { id: 'armor', label: 'Armure' },
  { id: 'food', label: 'Nourriture' },
  { id: 'recipe', label: 'Recette' },
];

const CATEGORIES = [
  { value: '', label: 'Inchangée' },
  { value: 'construction', label: 'Construction' },
  { value: 'equipment', label: 'Équipement' },
  { value: 'items', label: 'Objets' },
  { value: 'nature', label: 'Nature' },
];
const ENCHANT_SLOTS = ['', 'sword', 'axe', 'pickaxe', 'shovel', 'hoe', 'bow', 'crossbow', 'trident', 'fishing_rod', 'armor_head', 'armor_torso', 'armor_legs', 'armor_feet', 'elytra', 'shield', 'all'];
const TOOL_KINDS = [
  { value: '', label: 'Aucun' },
  { value: 'pickaxe', label: 'Pioche' },
  { value: 'axe', label: 'Hache' },
  { value: 'shovel', label: 'Pelle' },
  { value: 'hoe', label: 'Houe' },
];
const ARMOR_SLOTS = [
  { value: '', label: 'Aucun' },
  { value: 'slot.armor.head', label: 'Tête' },
  { value: 'slot.armor.chest', label: 'Torse' },
  { value: 'slot.armor.legs', label: 'Jambes' },
  { value: 'slot.armor.feet', label: 'Pieds' },
];

function readAsDataUrl(file: File): Promise<string> {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result as string);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
}
function readAsText(file: File): Promise<string> {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result as string);
    r.onerror = rej;
    r.readAsText(file);
  });
}

export function ItemEditor({ item, groupId, onSaved, readonlyResource }: { item: PackItemDTO; groupId: string; onSaved?: () => void; readonlyResource?: boolean }) {
  const [icon, setIcon] = useState(item.iconKey || '');
  const [iconPath, setIconPath] = useState<string | null>(item.iconPath);
  const [category, setCategory] = useState('');
  const [maxStack, setMaxStack] = useState('');
  const [tags, setTags] = useState('');
  const [nameEn, setNameEn] = useState('');
  const [nameFr, setNameFr] = useState('');
  const [durability, setDurability] = useState('');
  const [damage, setDamage] = useState('');
  const [enchantSlot, setEnchantSlot] = useState('');
  const [enchantValue, setEnchantValue] = useState('');
  const [handEquipped, setHandEquipped] = useState(false);
  const [toolKind, setToolKind] = useState('');
  const [toolTier, setToolTier] = useState('');
  const [toolMultiplier, setToolMultiplier] = useState('');
  const [armorSlot, setArmorSlot] = useState('');
  const [armorProtection, setArmorProtection] = useState('');
  const [armorToughness, setArmorToughness] = useState('');
  const [foodNutrition, setFoodNutrition] = useState('');
  const [foodSaturation, setFoodSaturation] = useState('');
  const [foodAlwaysEat, setFoodAlwaysEat] = useState(false);

  const [itemTextures, setItemTextures] = useState<PackTextureRef[]>([]);
  const [geometries, setGeometries] = useState<PackGeometryRef[]>([]);
  // Attachable (rendu 3D : armure équipée ou item tenu).
  const [attGeometry, setAttGeometry] = useState('');
  const [attTexture, setAttTexture] = useState('');
  const [attEnchanted, setAttEnchanted] = useState('');
  const [stagedAttGeo, setStagedAttGeo] = useState<File | null>(null);
  const [savingAtt, setSavingAtt] = useState(false);

  const [tab, setTab] = useState<ItemTab>('info');
  // Onglet Recette gardé monté une fois ouvert (brouillon conservé en changeant d'onglet).
  const [mountedTabs, setMountedTabs] = useState<Set<ItemTab>>(() => new Set<ItemTab>());
  const [dirty, setDirty] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [feedback, setFeedback] = useState<{ ok: boolean; msg: string } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const attTexRef = useRef<HTMLInputElement>(null);
  const attGeoRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    apiService.getPackItemTextures(groupId).then((r) => setItemTextures(r.data?.textures ?? []));
    apiService.getPackGeometries(groupId).then((r) => setGeometries(r.data?.geometries ?? []));
    // Attachable existant : on préremplit la géométrie (l'id brut est réutilisable).
    apiService.getPackAttachable(groupId, item.identifier).then((r) => {
      if (r.data?.exists && r.data.geometry) setAttGeometry(r.data.geometry);
    });
    // Convention Paladium : clé item SANS suffixe .name (les blocks utilisent tile.<id>.name).
    apiService.getPackLang(groupId, `item.${item.identifier}`).then((r) => {
      setNameEn(r.data?.translations?.en_US ?? '');
      setNameFr(r.data?.translations?.fr_FR ?? '');
    });
    if (item.source) {
      apiService.getPackFileJson(groupId, item.source).then((r) => {
        const it = (r.data as any)?.['minecraft:item'];
        const d = it?.description ?? {};
        const c = it?.components ?? {};
        if (d.creative_category) setCategory(String(d.creative_category));
        const ms = c['minecraft:max_stack_size'];
        if (ms != null) setMaxStack(String(typeof ms === 'object' ? ms.value : ms));
        const tg = c['minecraft:tags']?.tags;
        if (Array.isArray(tg)) setTags(tg.join(', '));
        const dur = c['minecraft:durability']?.max_durability;
        if (dur != null) setDurability(String(dur));
        const dmg = c['minecraft:damage'];
        const dv = typeof dmg === 'object' ? dmg?.damage : dmg;
        if (dv != null) setDamage(String(dv));
        const en = c['minecraft:enchantable'];
        if (en) { setEnchantSlot(en.slot || ''); if (en.value != null) setEnchantValue(String(en.value)); }
        const he = c['minecraft:hand_equipped'];
        setHandEquipped(!!(he && (he.enabled ?? he)));
        for (const k of ['pickaxe', 'axe', 'shovel', 'hoe']) {
          const t = c['minecraft:' + k];
          if (t) { setToolKind(k); if (t.tier != null) setToolTier(String(t.tier)); if (t.multiplier != null) setToolMultiplier(String(t.multiplier)); break; }
        }
        const w = c['minecraft:wearable'];
        if (w) { setArmorSlot(w.slot || ''); if (w.protection != null) setArmorProtection(String(w.protection)); if (w.toughness != null) setArmorToughness(String(w.toughness)); }
        const fd = c['minecraft:food'];
        if (fd) { if (fd.nutrition != null) setFoodNutrition(String(fd.nutrition)); if (fd.saturation != null) setFoodSaturation(String(fd.saturation)); setFoodAlwaysEat(!!fd.can_always_eat); }
      });
    }
  }, [groupId, item.identifier, item.source]);

  const uploadIcon = async (file: File) => {
    setUploading(true);
    setFeedback(null);
    const res = await apiService.importPackTexture(groupId, { key: file.name.replace(/\.[^.]+$/, ''), fileName: file.name, fileData: await readAsDataUrl(file), atlas: 'item' });
    setUploading(false);
    if (res.data?.success && res.data.value) {
      clearPackTextureCache(groupId);
      setIcon(res.data.value);
      setIconPath(null);
    } else {
      setFeedback({ ok: false, msg: res.data?.message || res.error || 'Échec import' });
    }
  };

  const num = (v: string) => (v === '' ? null : Number(v));

  const save = async (): Promise<boolean> => {
    setSaving(true);
    setFeedback(null);
    const res = await apiService.saveItemForm(groupId, {
      identifier: item.identifier,
      source: item.source,
      icon,
      translations: { en_US: nameEn, fr_FR: nameFr },
      creativeCategory: category || null,
      tags: tags.split(/[,\n]/).map((t) => t.trim()).filter(Boolean),
      maxStack: num(maxStack),
      durability: num(durability),
      damage: num(damage),
      enchantSlot: enchantSlot || null,
      enchantValue: num(enchantValue),
      handEquipped,
      toolKind: toolKind || null,
      toolTier: num(toolTier),
      toolMultiplier: num(toolMultiplier),
      armorSlot: armorSlot || null,
      armorProtection: num(armorProtection),
      armorToughness: num(armorToughness),
      foodNutrition: num(foodNutrition),
      foodSaturation: num(foodSaturation),
      foodAlwaysEat,
    });
    setSaving(false);
    if (res.data?.success) {
      setDirty(false);
      setFeedback({ ok: true, msg: res.data.message });
      onSaved?.();
      return true;
    }
    setFeedback({ ok: false, msg: res.data?.message || res.error || 'Échec' });
    return false;
  };

  // Suivi des modifications non enregistrées → barre « Enregistrer tout ».
  useUnsavedDraft({ kind: 'item', groupId, identifier: item.identifier, label: item.identifier, dirty, save });

  const publish = async () => {
    setPublishing(true);
    setFeedback(null);
    const res = await apiService.publishPackGroup(groupId);
    setPublishing(false);
    setFeedback(res.data ? { ok: res.data.success, msg: res.data.messages.join(' · ') } : { ok: false, msg: res.error || 'Échec publication' });
  };

  const geoOptions = geometries.map((g) => ({ value: g.id, label: g.id.replace(/^geometry\./, ''), hint: g.id }));
  const iconOptions = itemTextures.map((t) => ({ value: t.key, label: t.key, thumbnailPath: t.path }));
  const iconThumbPath = itemTextures.find((t) => t.key === icon)?.path ?? iconPath;

  const uploadAttTexture = async (file: File) => {
    const res = await apiService.importPackTexture(groupId, {
      key: file.name.replace(/\.[^.]+$/, ''), fileName: file.name, fileData: await readAsDataUrl(file), atlas: 'entity',
    });
    if (res.data?.success && res.data.value) { clearPackTextureCache(groupId); setAttTexture(res.data.value); }
    else setFeedback({ ok: false, msg: res.data?.message || res.error || 'Échec import texture' });
  };

  const saveAttachable = async () => {
    setSavingAtt(true);
    setFeedback(null);
    let geo = attGeometry;
    try {
      if (stagedAttGeo) {
        const r = await apiService.importPackGeometry(groupId, await readAsText(stagedAttGeo));
        if (!r.data?.success || !r.data.value) { setSavingAtt(false); setFeedback({ ok: false, msg: r.data?.message || r.error || 'Échec import géométrie' }); return; }
        geo = r.data.value;
      }
    } catch {
      setSavingAtt(false);
      setFeedback({ ok: false, msg: "Échec de l'intégration de la géométrie" });
      return;
    }
    if (!geo || !attTexture) { setSavingAtt(false); setFeedback({ ok: false, msg: 'Géométrie et texture requises pour l’attachable.' }); return; }
    const res = await apiService.savePackAttachable(groupId, { identifier: item.identifier, geometry: geo, texture: attTexture, enchantedTexture: attEnchanted || undefined });
    setSavingAtt(false);
    if (res.data?.success) {
      setAttGeometry(geo);
      setStagedAttGeo(null);
      clearPackTextureCache(groupId);
      if (stagedAttGeo) apiService.getPackGeometries(groupId).then((r) => setGeometries(r.data?.geometries ?? []));
      setFeedback({ ok: true, msg: res.data.message });
      onSaved?.();
    } else {
      setFeedback({ ok: false, msg: res.data?.message || res.error || 'Échec' });
    }
  };

  return (
    <div className={styles.form} onChange={() => setDirty(true)} onInput={() => setDirty(true)}>
      <div className={styles.tabs}>
        {TABS.map((t) => (
          <button key={t.id} type="button" className={`${styles.tab} ${tab === t.id ? styles.tabActive : ''}`} onClick={() => { setTab(t.id); setMountedTabs((s) => (s.has(t.id) ? s : new Set(s).add(t.id))); setFeedback(null); }}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'info' && (
        <>
          <div className={styles.field}>
            <label className={styles.label}>Icône (clé de texture)</label>
            {readonlyResource ? (
              <div className={styles.pickerRow}>
                {icon && <PackThumbnail groupId={groupId} path={iconThumbPath} size={36} />}
                <input className={`${styles.input} ${styles.pickerGrow}`} value={icon} readOnly disabled />
              </div>
            ) : (
              <div className={styles.pickerRow}>
                {icon && <PackThumbnail groupId={groupId} path={iconThumbPath} size={36} />}
                <div className={styles.pickerGrow}>
                  <SearchPicker groupId={groupId} value={icon} onChange={(v) => { setIcon(v); setIconPath(null); setDirty(true); }} options={iconOptions} showThumbnails placeholder="Clé d'icône…" />
                </div>
                <button className={styles.uploadBtn} disabled={uploading} onClick={() => fileRef.current?.click()}>
                  {uploading ? <Loader2 size={14} className={styles.spin} /> : <Upload size={14} />} Importer
                  <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={(e) => e.target.files?.[0] && uploadIcon(e.target.files[0])} />
                </button>
              </div>
            )}
          </div>

          <div className={styles.row2}>
            <div className={styles.field}>
              <label className={styles.label}>Catégorie créative</label>
              <select className={styles.select} value={category} onChange={(e) => setCategory(e.target.value)}>
                {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            <div className={styles.field}>
              <label className={styles.label}>Taille de pile max</label>
              <input className={styles.input} type="number" min={1} max={64} value={maxStack} onChange={(e) => setMaxStack(e.target.value)} placeholder="inchangé" />
            </div>
          </div>

          <div className={styles.field}>
            <label className={styles.label}>Tags (séparés par des virgules)</label>
            <input className={styles.input} value={tags} onChange={(e) => setTags(e.target.value)} placeholder="ex. minecraft:is_pickaxe" />
          </div>

          <div className={styles.row2}>
            <div className={styles.field}>
              <label className={styles.label}>Nom affiché (EN)</label>
              <input className={styles.input} value={nameEn} onChange={(e) => setNameEn(e.target.value)} disabled={readonlyResource} />
            </div>
            <div className={styles.field}>
              <label className={styles.label}>Nom affiché (FR)</label>
              <input className={styles.input} value={nameFr} onChange={(e) => setNameFr(e.target.value)} disabled={readonlyResource} />
            </div>
          </div>
        </>
      )}

      {tab === 'tool' && (
        <>
          <div className={styles.row2}>
            <div className={styles.field}>
              <label className={styles.label}>Durabilité (max)</label>
              <input className={styles.input} type="number" min={1} value={durability} onChange={(e) => setDurability(e.target.value)} placeholder="ex. 1750" />
            </div>
            <div className={styles.field}>
              <label className={styles.label}>Dégâts d'attaque</label>
              <input className={styles.input} type="number" min={0} value={damage} onChange={(e) => setDamage(e.target.value)} placeholder="ex. 7" />
            </div>
          </div>

          <div className={styles.row2}>
            <div className={styles.field}>
              <label className={styles.label}>Enchantable — slot</label>
              <select className={styles.select} value={enchantSlot} onChange={(e) => setEnchantSlot(e.target.value)}>
                {ENCHANT_SLOTS.map((s) => <option key={s} value={s}>{s || 'Aucun'}</option>)}
              </select>
            </div>
            <div className={styles.field}>
              <label className={styles.label}>Enchantabilité (valeur)</label>
              <input className={styles.input} type="number" min={0} value={enchantValue} onChange={(e) => setEnchantValue(e.target.value)} placeholder="ex. 15" />
            </div>
          </div>

          <label className={styles.checkRow}>
            <input type="checkbox" checked={handEquipped} onChange={(e) => setHandEquipped(e.target.checked)} />
            <span>Tenu en main (hand_equipped)</span>
          </label>

          <div className={styles.field}>
            <label className={styles.label}>Outil de minage</label>
            <select className={styles.select} value={toolKind} onChange={(e) => setToolKind(e.target.value)}>
              {TOOL_KINDS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          {toolKind && (
            <div className={styles.row2}>
              <div className={styles.field}>
                <label className={styles.label}>Palier (tier)</label>
                <input className={styles.input} type="number" min={0} value={toolTier} onChange={(e) => setToolTier(e.target.value)} placeholder="ex. 5" />
              </div>
              <div className={styles.field}>
                <label className={styles.label}>Multiplicateur de vitesse</label>
                <input className={styles.input} type="number" min={1} value={toolMultiplier} onChange={(e) => setToolMultiplier(e.target.value)} placeholder="ex. 12" />
              </div>
            </div>
          )}
        </>
      )}

      {tab === 'armor' && (
        <>
          <div className={styles.field}>
            <label className={styles.label}>Emplacement d'armure</label>
            <select className={styles.select} value={armorSlot} onChange={(e) => setArmorSlot(e.target.value)}>
              {ARMOR_SLOTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
          {armorSlot && (
            <div className={styles.row2}>
              <div className={styles.field}>
                <label className={styles.label}>Protection</label>
                <input className={styles.input} type="number" min={0} value={armorProtection} onChange={(e) => setArmorProtection(e.target.value)} placeholder="ex. 3" />
              </div>
              <div className={styles.field}>
                <label className={styles.label}>Toughness (résistance)</label>
                <input className={styles.input} type="number" min={0} value={armorToughness} onChange={(e) => setArmorToughness(e.target.value)} placeholder="ex. 2" />
              </div>
            </div>
          )}

          <div className={styles.attSection}>
            <div className={styles.attTitle}>Rendu 3D — attachable (armure équipée ou item tenu, style prototype)</div>

            <div className={styles.field}>
              <label className={styles.label}>Géométrie de l'attachable</label>
              {stagedAttGeo ? (
                <div className={styles.stagedFile}>
                  <span className={styles.stagedName}>{stagedAttGeo.name}</span>
                  <span className={styles.stagedTag}>à intégrer</span>
                  <button type="button" className={styles.stagedDel} onClick={() => setStagedAttGeo(null)} title="Supprimer"><Trash2 size={14} /></button>
                </div>
              ) : (
                <div className={styles.pickerRow}>
                  <div className={styles.pickerGrow}>
                    <SearchPicker groupId={groupId} value={attGeometry} onChange={(v) => { setAttGeometry(v); setDirty(true); }} options={geoOptions} placeholder="geometry.… (ex. geometry.proto.paladium.armor.boots)" />
                  </div>
                  <button className={styles.uploadBtn} disabled={readonlyResource} onClick={() => attGeoRef.current?.click()}>
                    <Upload size={14} /> Importer
                    <input ref={attGeoRef} type="file" accept=".json,.geo.json,application/json" onChange={(e) => e.target.files?.[0] && setStagedAttGeo(e.target.files[0])} />
                  </button>
                </div>
              )}
            </div>

            <div className={styles.field}>
              <label className={styles.label}>Texture (chemin dans le pack)</label>
              <div className={styles.pickerRow}>
                <input className={`${styles.input} ${styles.pickerGrow}`} value={attTexture} onChange={(e) => setAttTexture(e.target.value)} placeholder="textures/models/armors/… (sans .png)" disabled={readonlyResource} />
                {!readonlyResource && (
                  <button className={styles.uploadBtn} onClick={() => attTexRef.current?.click()}>
                    <Upload size={14} /> Importer
                    <input ref={attTexRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={(e) => e.target.files?.[0] && uploadAttTexture(e.target.files[0])} />
                  </button>
                )}
              </div>
            </div>

            <div className={styles.field}>
              <label className={styles.label}>Texture enchantée (optionnel)</label>
              <input className={styles.input} value={attEnchanted} onChange={(e) => setAttEnchanted(e.target.value)} placeholder="textures/misc/enchanted_item_glint (défaut)" disabled={readonlyResource} />
            </div>

            {!readonlyResource && (
              <button className={styles.saveBtn} onClick={saveAttachable} disabled={savingAtt}>
                {savingAtt ? <Loader2 size={14} className={styles.spin} /> : <Save size={14} />}
                {savingAtt ? 'Enregistrement…' : 'Enregistrer l’attachable'}
              </button>
            )}
          </div>
        </>
      )}

      {tab === 'food' && (
        <>
          <div className={styles.row2}>
            <div className={styles.field}>
              <label className={styles.label}>Nutrition (points de faim)</label>
              <input className={styles.input} type="number" min={0} value={foodNutrition} onChange={(e) => setFoodNutrition(e.target.value)} placeholder="ex. 6" />
            </div>
            <div className={styles.field}>
              <label className={styles.label}>Saturation</label>
              <input className={styles.input} type="number" min={0} step={0.1} value={foodSaturation} onChange={(e) => setFoodSaturation(e.target.value)} placeholder="ex. 7.2" />
            </div>
          </div>
          <label className={styles.checkRow}>
            <input type="checkbox" checked={foodAlwaysEat} onChange={(e) => setFoodAlwaysEat(e.target.checked)} />
            <span>Mangeable même rassasié (can_always_eat)</span>
          </label>
        </>
      )}

      {mountedTabs.has('recipe') && (
        <div style={tab === 'recipe' ? undefined : { display: 'none' }}>
          <RecipeEditor identifier={item.identifier} thumbnail={iconThumbPath} groupId={groupId} draftKind="item" draftScope="recipe" onSaved={onSaved} />
        </div>
      )}

      {tab !== 'recipe' && (
        <>
          {feedback && (
            <div className={feedback.ok ? styles.ok : styles.error}>
              {feedback.ok ? <Check size={14} /> : <AlertTriangle size={14} />} {feedback.msg}
            </div>
          )}

          <div className={styles.actions}>
            <button className={styles.saveBtn} onClick={save} disabled={saving}>
              {saving ? <Loader2 size={14} className={styles.spin} /> : <Save size={14} />}
              {saving ? 'Enregistrement…' : 'Enregistrer'}
            </button>
            <button className={styles.publishBtn} onClick={publish} disabled={publishing}>
              {publishing ? <Loader2 size={14} className={styles.spin} /> : <UploadCloud size={14} />}
              {publishing ? 'Publication…' : 'Publier sur git'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
